/**
 * Public landing page — a premium, multi-section brand homepage for the
 * student-run shop. Signed-in users are routed straight to their home
 * (teachers/admins → /order, baristas → /kitchen); everyone else gets the
 * marketing surface. All brand-original: WCA red + gold on a warm chalkboard,
 * with the chipped-mug motif.
 */
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, Coffee, Leaf, Zap } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export function Landing() {
  const { isAuthed, role, isLoading } = useAuth();
  // Send any signed-in user off the marketing page. Baristas head to the
  // kitchen; everyone else (including the rare bootstrap-failed role === null
  // case) goes to /order, where RoleGuard surfaces a retryable error rather
  // than leaving them stranded here while actually logged in.
  if (isAuthed && !isLoading) {
    return <Navigate to={role === 'barista' ? '/kitchen' : '/order'} replace />;
  }

  return (
    <div className="relative overflow-x-hidden bg-wca-chalk">
      <nav className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-5 py-5 sm:px-10">
        <span className="font-display text-xl text-wca-chalk sm:text-2xl">
          The Cracked Mug<span className="text-wca-gold">.</span>
        </span>
        <Link
          to="/login"
          className="rounded-full border border-wca-chalk/30 px-5 py-2 text-sm font-semibold text-wca-chalk transition-colors hover:bg-wca-chalk/10"
        >
          Sign in
        </Link>
      </nav>

      <Hero />
      <MenuTeaser />
      <ClosingCta />
      <SiteFooter />
    </div>
  );
}

function Hero() {
  return (
    <section className="cm-hero relative overflow-hidden text-wca-chalk">
      <div className="mx-auto grid min-h-dvh max-w-6xl grid-cols-1 items-center gap-10 px-6 pb-16 pt-28 sm:px-10 md:grid-cols-[1.1fr_0.9fr]">
        <div className="min-w-0 max-w-xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-wca-gold sm:text-xs sm:tracking-[0.28em]">
            Wesleyan Christian Academy · Est. 2026
          </p>
          <h1 className="mt-5 font-display font-black leading-[0.92] tracking-tight">
            <span className="block text-2xl font-medium italic opacity-85 sm:text-3xl">
              Coffee, delivered
            </span>
            <span className="-mt-1 block text-[2.75rem] leading-[0.95] sm:text-7xl sm:leading-[0.92] md:text-8xl">
              to your room<span className="text-wca-gold">.</span>
            </span>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-wca-chalk/80 sm:text-lg">
            The student-run coffee shop, now on your phone. Order from your classroom and a barista
            brings it to your door — or have it waiting at the counter.
          </p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/login"
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-wca-red px-7 py-4 text-base font-semibold text-white shadow-[0_10px_28px_-8px_rgba(196,18,48,0.55)] transition-all hover:bg-wca-red-dark active:scale-[0.98] sm:w-auto"
            >
              Sign in to order
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/menu"
              className="w-full rounded-full border border-wca-chalk/30 px-7 py-4 text-center text-base font-semibold text-wca-chalk transition-colors hover:bg-wca-chalk/10 sm:w-auto"
            >
              See the menu
            </Link>
          </div>
        </div>

        <div className="relative hidden items-center justify-center md:flex">
          <div className="pointer-events-none absolute left-1/2 top-[14%] h-48 w-48 -translate-x-1/2 rounded-full bg-wca-chalk/15 blur-3xl" />
          <ChippedMug className="relative h-72 w-72 lg:h-80 lg:w-80" />
        </div>
      </div>
    </section>
  );
}

const CATEGORIES = [
  {
    icon: Coffee,
    name: 'Espresso',
    tag: 'Lattes · mochas · more',
    cls: 'from-[#6f4322] to-[#2a1810]',
  },
  {
    icon: Leaf,
    name: 'Non-Espresso',
    tag: 'Tea · chocolate · cider',
    cls: 'from-[#3f6f53] to-[#1c3326]',
  },
  { icon: Zap, name: 'Energy', tag: 'Cold & loaded', cls: 'from-[#b8324a] to-[#7a1228]' },
];

function MenuTeaser() {
  return (
    <section className="border-y border-wca-gray-light bg-wca-surface px-6 py-20 sm:px-10 sm:py-24">
      <Eyebrow>On the board</Eyebrow>
      <SectionTitle>Something for every shift</SectionTitle>
      <SectionSub>
        Espresso classics, non-espresso favorites, and a jolt of energy — all made to order.
      </SectionSub>
      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-5 sm:grid-cols-3">
        {CATEGORIES.map((cat) => (
          <div
            key={cat.name}
            className={`relative flex min-h-[180px] flex-col justify-end overflow-hidden rounded-2xl bg-gradient-to-br ${cat.cls} p-6 text-white shadow-[0_14px_32px_-14px_rgba(26,26,26,0.4)]`}
          >
            <cat.icon
              size={28}
              strokeWidth={1.6}
              className="absolute right-5 top-5 opacity-50"
              aria-hidden="true"
            />
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] opacity-85">
              {cat.tag}
            </p>
            <h3 className="mt-1 font-display text-3xl">{cat.name}</h3>
          </div>
        ))}
      </div>
      <p className="mt-7 text-center text-base text-wca-black">
        …or <span className="font-semibold text-wca-red">Build Your Own</span> — pick a base, milk,
        flavors, and toppings, your way.
      </p>
    </section>
  );
}

function ClosingCta() {
  return (
    <section className="cm-closing px-6 py-24 text-center text-wca-chalk">
      <h2 className="font-display text-4xl tracking-tight sm:text-5xl">Ready for a better cup?</h2>
      <p className="mx-auto mt-3 max-w-md text-lg text-wca-chalk/80">
        Sign in with your school account and order your first one.
      </p>
      <Link
        to="/login"
        className="group mt-8 inline-flex items-center gap-2 rounded-full bg-wca-red px-8 py-4 text-base font-semibold text-white shadow-[0_10px_28px_-8px_rgba(196,18,48,0.55)] transition-all hover:bg-wca-red-dark active:scale-[0.98]"
      >
        Sign in to order
        <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
      </Link>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-[#111] px-6 py-11 text-wca-chalk/70 sm:px-10">
      <div className="mx-auto flex max-w-5xl flex-col justify-between gap-7 sm:flex-row">
        <div>
          <span className="font-display text-xl text-wca-chalk">
            The Cracked Mug<span className="text-wca-gold">.</span>
          </span>
          <p className="mt-2 max-w-xs text-sm leading-relaxed">
            A student-run coffee shop at Wesleyan Christian Academy, High Point, NC.
          </p>
        </div>
        <nav className="flex gap-7 text-sm">
          <Link to="/login" className="transition-colors hover:text-wca-chalk">
            Sign in
          </Link>
          <Link to="/privacy" className="transition-colors hover:text-wca-chalk">
            Privacy
          </Link>
        </nav>
      </div>
      <div className="mx-auto mt-7 flex max-w-5xl flex-col justify-between gap-2 border-t border-white/10 pt-5 text-[11px] text-wca-chalk/45 sm:flex-row">
        <span>© 2026 The Cracked Mug · Wesleyan Christian Academy</span>
        <span>Made by students, for teachers</span>
      </div>
    </footer>
  );
}

// ─── Small shared bits ───────────────────────────────────────────────────────

function Eyebrow({ children }: { children: string }) {
  return (
    <p className="text-center font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-wca-red">
      {children}
    </p>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <h2 className="mt-3 text-center font-display text-4xl tracking-tight text-wca-black sm:text-[44px]">
      {children}
    </h2>
  );
}

function SectionSub({ children }: { children: string }) {
  return (
    <p className="mx-auto mb-12 mt-2 max-w-xl text-center text-base leading-relaxed text-wca-gray-mid sm:text-lg">
      {children}
    </p>
  );
}

/** The chipped-mug motif in chalk + gold for the dark hero. */
function ChippedMug({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 200 200" fill="none" className={className} aria-hidden="true">
      <g
        stroke="rgba(245,242,236,0.9)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M 50 62 L 50 150 Q 50 168 68 168 L 122 168 Q 140 168 140 150 L 140 62" />
        <path d="M 140 82 Q 178 82 178 108 Q 178 134 140 134" />
      </g>
      <path
        d="M 50 62 L 60 58 L 70 64 L 82 56 L 95 64 L 108 56 L 122 64 L 132 56 L 140 62"
        stroke="#d4a843"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="60"
        y1="80"
        x2="100"
        y2="80"
        stroke="rgba(245,242,236,0.45)"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </svg>
  );
}
